"use client";

import { AnimatePresence, motion, Reorder } from "framer-motion";

import { Button } from "@acme/ui/button";

import { makeEditorId } from "../../_hooks/use-monaco-editor";
import { useStudioStore } from "../../_hooks/use-studio-store";
import { FileTab } from "./file-tab";

interface TestTabsGroupProps {
  isTestGroupOpen: boolean;
  setIsTestGroupOpen: (state: boolean) => void;

  registerTabRef: (id: string, el: HTMLDivElement | null) => void;
  onContextMenu?: (tab: { id: string; index: number; type: "source" }) => void;
}

export const TestTabsGroup = ({
  isTestGroupOpen,
  setIsTestGroupOpen,

  registerTabRef,
  onContextMenu,
}: TestTabsGroupProps) => {
  const sources = useStudioStore((s) => s.sources);
  const removeSource = useStudioStore((s) => s.removeSource);
  const setSources = useStudioStore((s) => s.setSources);

  return (
    <div className="relative flex w-fit min-w-20 gap-1">
      <div className="sticky left-2 z-30 mr-4 flex h-[36px]">
        <Button
          onClick={() => setIsTestGroupOpen(!isTestGroupOpen)}
          type="button"
          size="sm"
          className="bg-foreground/75 text-background hover:bg-foreground/30 dark:bg-foreground mt-1 ml-2 h-6 cursor-pointer px-2 py-1 text-xs font-medium"
        >
          Tests
        </Button>
      </div>
      <AnimatePresence>
        {isTestGroupOpen && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <Reorder.Group
              axis="x"
              values={sources}
              onReorder={(newOrder) => setSources(newOrder)}
              className="-ml-2 flex h-full"
            >
              {sources.map((source, index) => {
                const tabId = makeEditorId(source.id, "source");

                return (
                  <Reorder.Item
                    key={tabId}
                    value={source}
                    className="-ml-4 first:ml-0"
                  >
                    <FileTab
                      value={tabId}
                      ref={(el) => registerTabRef(tabId, el)}
                      onRemove={() => removeSource(source.id)}
                      onContextMenu={() =>
                        onContextMenu?.({ id: tabId, index, type: "source" })
                      }
                      name={source.name ?? `Test ${index + 1}`}
                      disabledRemove={sources.length <= 1}
                      type="test"
                    />
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </motion.div>
        )}
      </AnimatePresence>

      <i className="bg-foreground/75 shadow-foreground dark:bg-foreground absolute right-0 -bottom-px left-0 z-10 h-px shadow-[0px_1px_0px_0px] transition-all" />
    </div>
  );
};
